/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — stmt-cashflow.ts
   buildCashFlow() and related non-cash detection, working capital helpers
   ═══════════════════════════════════════════════════════════════════════ */

import { state, type SectionData, type LineItem } from './state';
import {
  prepareStatementData,
  restoreStatementData,
} from './consolidation';
import {
  sum,
  rows,
  hasPriorData,
  computeNetIncome,
  tr,
  sectionHeader,
  columnHeaders,
  stmtHeader,
  el,
} from './utils';
import { mergeLabels, type MergedItem } from './stmt-income';
import {
  CASH_PATTERN,
  DEBT_PATTERN,
  NONCASH_PATTERNS,
  type NoncashPatternEntry,
} from './patterns';

// ─── Types ─────────────────────────────────────────────────────────────────

interface NoncashItem {
  label: string;
  amount: number;
}

interface WorkingCapitalChange {
  label: string;
  amount: number;
}

export interface CashFlowLineItem {
  label: string;
  amount: number;
  priorAmount: number;
}

// ─── Cash Flow Detection Patterns ─────────────────────────────────────────
const ACCUM_DEP_PATTERN: RegExp =
  /accum|accumulated|accum\.?\s*dep|a\/d/i;

const LEASE_LIABILITY_PATTERN: RegExp =
  /lease\s*liab|lease\s*obligation|right.of.use\s*liab|rou\s*liab|finance\s*lease|operating\s*lease\s*liab/i;

const RETAINED_EARNINGS_PATTERN: RegExp =
  /retained\s*earnings|accumulated\s*(deficit|surplus)|^\s*r\.?e\.?\s*$/i;

/** AOCI and similar non-cash equity items — excluded from financing activities */
const NONCASH_EQUITY_PATTERN: RegExp =
  /accumulated\s*other\s*comprehensive|aoci|unrealized\s*(gain|loss).*equity/i;

/** Items in noncurrent-assets that are NOT investing activities */
const NON_INVESTING_ASSET_PATTERN: RegExp =
  /deferred\s*tax|right.of.use|rou\s*asset|operating\s*lease\s*asset/i;

/** Short-term investment accounts — classified as investing, not operating WC */
const STI_PATTERN: RegExp =
  /short.term\s*invest|marketable\s*secur|trading\s*secur/i;

// ─── Non-cash Detection ──────────────────────────────────────────────────
// NONCASH_PATTERNS imported from ./patterns

/** Skip contra-asset accounts whose expense is already captured as a non-cash add-back */
const CONTRA_ASSET_PATTERN: RegExp =
  /allowance\s*for|accum.*depreciation|accum.*amortization|contra|provision\s*for\s*(bad|doubtful)/i;

export function findNoncashItems(
  dataObj: SectionData,
  manualLabels?: string[]
): NoncashItem[] {
  const items: NoncashItem[] = [];
  const seen = new Set<string>();
  (['opex', 'other'] as const).forEach((section: string) => {
    rows(dataObj, section).forEach((row: LineItem) => {
      NONCASH_PATTERNS.forEach((rule: NoncashPatternEntry) => {
        if (rule.pattern.test(row.label) && !seen.has(rule.label)) {
          // Skip if already manually mapped to cf-operating
          const isDuplicate: boolean =
            !!manualLabels &&
            manualLabels.some((ml: string) => rule.pattern.test(ml));
          if (isDuplicate) return;
          seen.add(rule.label);
          const amt: number = parseFloat(String(row.amount)) || 0;
          // Non-cash expenses are added back (positive); gains are subtracted (negative)
          if (rule.negate) {
            items.push({ label: row.label, amount: -Math.abs(amt) });
          } else {
            items.push({ label: row.label, amount: Math.abs(amt) });
          }
        }
      });
    });
  });
  return items;
}

export function computeWorkingCapitalChanges(
  curData: SectionData,
  priorDataObj: SectionData
): WorkingCapitalChange[] {
  const changes: WorkingCapitalChange[] = [];

  // Current assets: decrease = cash inflow (+), increase = cash outflow (-)
  const curCA: LineItem[] = rows(curData, 'current-assets');
  const priorCA: LineItem[] = rows(priorDataObj, 'current-assets');
  const caItems: MergedItem[] = mergeLabels(curCA, priorCA, true);
  caItems.forEach((item: MergedItem) => {
    if (CASH_PATTERN.test(item.label)) return;
    if (CONTRA_ASSET_PATTERN.test(item.label)) return; // Already captured as non-cash add-back
    if (STI_PATTERN.test(item.label)) return; // Classified as investing, not operating
    const curAmt: number = item.cur || 0;
    const priorAmt: number = (item.prior as number) || 0;
    const change: number = priorAmt - curAmt; // decrease in asset = positive CF
    if (Math.abs(change) > 0.005) {
      const direction: string = change > 0 ? 'Decrease' : 'Increase';
      changes.push({
        label: direction + ' in ' + item.label,
        amount: parseFloat(change.toFixed(2)),
      });
    }
  });

  // Current liabilities: increase = cash inflow (+), decrease = cash outflow (-)
  // Liabilities stored as negative (credit-normal via SECTION_SIGN = -1)
  const curCL: LineItem[] = rows(curData, 'current-liab');
  const priorCL: LineItem[] = rows(priorDataObj, 'current-liab');
  const clItems: MergedItem[] = mergeLabels(curCL, priorCL, true);
  clItems.forEach((item: MergedItem) => {
    // Skip debt-related current liabilities — handled in financing activities
    if (DEBT_PATTERN.test(item.label)) return;
    const curAmt: number = Math.abs(item.cur || 0);
    const priorAmt: number = Math.abs((item.prior as number) || 0);
    const change: number = curAmt - priorAmt; // increase in absolute liability = positive CF
    if (Math.abs(change) > 0.005) {
      const direction: string = change > 0 ? 'Increase' : 'Decrease';
      changes.push({
        label: direction + ' in ' + item.label,
        amount: parseFloat(change.toFixed(2)),
      });
    }
  });

  // Noncurrent operating liabilities (exclude debt and leases — those are in financing)
  const curNCL: LineItem[] = rows(curData, 'noncurrent-liab');
  const priorNCL: LineItem[] = rows(priorDataObj, 'noncurrent-liab');
  const nclItems: MergedItem[] = mergeLabels(curNCL, priorNCL, true);
  nclItems.forEach((item: MergedItem) => {
    if (DEBT_PATTERN.test(item.label)) return;
    if (LEASE_LIABILITY_PATTERN.test(item.label)) return;
    const curAmt: number = Math.abs(item.cur || 0);
    const priorAmt: number = Math.abs((item.prior as number) || 0);
    const change: number = curAmt - priorAmt;
    if (Math.abs(change) > 0.005) {
      const direction: string = change > 0 ? 'Increase' : 'Decrease';
      changes.push({
        label: direction + ' in ' + item.label,
        amount: parseFloat(change.toFixed(2)),
      });
    }
  });

  // Noncurrent operating assets that aren't investing (DTA, ROU)
  const curNCA: LineItem[] = rows(curData, 'noncurrent-assets');
  const priorNCA: LineItem[] = rows(priorDataObj, 'noncurrent-assets');
  const ncaItems: MergedItem[] = mergeLabels(curNCA, priorNCA, true);
  ncaItems.forEach((item: MergedItem) => {
    if (!NON_INVESTING_ASSET_PATTERN.test(item.label)) return;
    if (ACCUM_DEP_PATTERN.test(item.label)) return;
    const curAmt: number = item.cur || 0;
    const priorAmt: number = (item.prior as number) || 0;
    const change: number = priorAmt - curAmt; // decrease in asset = positive CF
    if (Math.abs(change) > 0.005) {
      const direction: string = change > 0 ? 'Decrease' : 'Increase';
      changes.push({
        label: direction + ' in ' + item.label,
        amount: parseFloat(change.toFixed(2)),
      });
    }
  });

  return changes;
}

// ─── Cash Flow Auto-Detection (Investing & Financing) ────────────────────

/**
 * Detects investing activities by comparing noncurrent-asset accounts
 * between current and prior periods. Excludes accumulated depreciation
 * accounts (handled as non-cash in operating).
 */
export function detectInvestingActivities(
  curData: SectionData,
  priorDataObj: SectionData
): CashFlowLineItem[] {
  const items: CashFlowLineItem[] = [];
  const curRows_: LineItem[] = rows(curData, 'noncurrent-assets');
  const priorRows_: LineItem[] = rows(priorDataObj, 'noncurrent-assets');
  const merged: MergedItem[] = mergeLabels(curRows_, priorRows_, true);

  merged.forEach((item: MergedItem) => {
    // Skip accumulated depreciation/amortization — handled in operating
    if (ACCUM_DEP_PATTERN.test(item.label)) return;
    // Skip non-investing assets (deferred tax, ROU leases) — handled in operating
    if (NON_INVESTING_ASSET_PATTERN.test(item.label)) return;

    const curAmt: number = item.cur || 0;
    const priorAmt: number = (item.prior as number) || 0;
    const delta: number = curAmt - priorAmt;

    if (Math.abs(delta) < 0.005) return;

    if (delta > 0) {
      // Asset increased → purchase (cash outflow = negative)
      items.push({
        label: 'Purchase of ' + item.label,
        amount: -delta,
        priorAmount: 0,
      });
    } else {
      // Asset decreased → disposal (cash inflow = positive)
      items.push({
        label: 'Proceeds from sale of ' + item.label,
        amount: -delta, // delta is negative, so -delta is positive
        priorAmount: 0,
      });
    }
  });

  // Short-term investments (current-assets) are also investing activities
  const curCA: LineItem[] = rows(curData, 'current-assets');
  const priorCA: LineItem[] = rows(priorDataObj, 'current-assets');
  const caMerged: MergedItem[] = mergeLabels(curCA, priorCA, true);
  caMerged.forEach((item: MergedItem) => {
    if (!STI_PATTERN.test(item.label)) return;
    const curAmt: number = item.cur || 0;
    const priorAmt: number = (item.prior as number) || 0;
    const delta: number = curAmt - priorAmt;
    if (Math.abs(delta) < 0.005) return;
    if (delta > 0) {
      items.push({ label: 'Purchase of ' + item.label, amount: -delta, priorAmount: 0 });
    } else {
      items.push({ label: 'Proceeds from ' + item.label, amount: -delta, priorAmount: 0 });
    }
  });

  return items;
}

/**
 * Detects financing activities by comparing noncurrent-liab and equity accounts
 * between current and prior periods.
 */
export function detectFinancingActivities(
  curData: SectionData,
  priorDataObj: SectionData
): CashFlowLineItem[] {
  const items: CashFlowLineItem[] = [];

  // ── Noncurrent liabilities: debt proceeds/repayments and lease payments ──
  const curNCL: LineItem[] = rows(curData, 'noncurrent-liab');
  const priorNCL: LineItem[] = rows(priorDataObj, 'noncurrent-liab');
  const nclMerged: MergedItem[] = mergeLabels(curNCL, priorNCL, true);

  nclMerged.forEach((item: MergedItem) => {
    const curAmt: number = Math.abs(item.cur || 0);
    const priorAmt: number = Math.abs((item.prior as number) || 0);
    const delta: number = curAmt - priorAmt;

    if (Math.abs(delta) < 0.005) return;

    if (DEBT_PATTERN.test(item.label)) {
      if (delta > 0) {
        items.push({ label: 'Proceeds from ' + item.label, amount: delta, priorAmount: 0 });
      } else {
        items.push({ label: 'Repayment of ' + item.label, amount: delta, priorAmount: 0 });
      }
    } else if (LEASE_LIABILITY_PATTERN.test(item.label)) {
      // Lease liability decrease = lease payment (outflow)
      if (delta < 0) {
        items.push({ label: 'Lease payments on ' + item.label, amount: delta, priorAmount: 0 });
      } else {
        items.push({ label: 'Increase in ' + item.label, amount: delta, priorAmount: 0 });
      }
    }
  });

  // ── Current portion of long-term debt ──
  const curCL: LineItem[] = rows(curData, 'current-liab');
  const priorCL: LineItem[] = rows(priorDataObj, 'current-liab');
  const clMerged: MergedItem[] = mergeLabels(curCL, priorCL, true);

  clMerged.forEach((item: MergedItem) => {
    if (!DEBT_PATTERN.test(item.label)) return;

    const curAmt: number = Math.abs(item.cur || 0);
    const priorAmt: number = Math.abs((item.prior as number) || 0);
    const delta: number = curAmt - priorAmt;

    if (Math.abs(delta) < 0.005) return;

    if (delta > 0) {
      items.push({ label: 'Proceeds from ' + item.label, amount: delta, priorAmount: 0 });
    } else {
      items.push({ label: 'Repayment of ' + item.label, amount: delta, priorAmount: 0 });
    }
  });

  // ── Equity changes (Retained Earnings handled separately for dividends) ──
  const curEq: LineItem[] = rows(curData, 'equity');
  const priorEq: LineItem[] = rows(priorDataObj, 'equity');
  const eqMerged: MergedItem[] = mergeLabels(curEq, priorEq, true);

  // Detect dividends/distributions from Retained Earnings change vs Net Income.
  // RE_change = NI - dividends, so dividends = NI - RE_change.
  // (Only valid when prior data exists.)
  let totalRE_cur: number = 0;
  let totalRE_prior: number = 0;
  let hasPriorRE: boolean = false;
  eqMerged.forEach((item: MergedItem) => {
    if (RETAINED_EARNINGS_PATTERN.test(item.label)) {
      totalRE_cur += item.cur || 0;
      const p = item.prior as number;
      if (p !== undefined && p !== null) {
        totalRE_prior += p;
        hasPriorRE = true;
      }
    }
  });

  if (hasPriorRE) {
    const reChange: number = totalRE_cur - totalRE_prior;
    const ni: number = computeNetIncomeForCF(curData);
    // Closed TB: RE_change = NI - dividends → dividends = NI - RE_change
    // Unclosed TB: RE_change = -dividends → dividends = -RE_change
    const isClosedTB = isTBClosed(curData);
    const dividends: number = isClosedTB ? (ni - reChange) : -reChange;
    if (Math.abs(dividends) > 0.005) {
      if (dividends > 0) {
        items.push({ label: 'Dividends/Distributions Paid', amount: -dividends, priorAmount: 0 });
      } else {
        // Negative "dividends" means RE grew faster than NI — likely capital contribution to RE
        items.push({ label: 'Capital Contribution to Retained Earnings', amount: -dividends, priorAmount: 0 });
      }
    }
  }

  // Other equity changes (Common Stock, APIC, Treasury) — exclude RE and AOCI
  eqMerged.forEach((item: MergedItem) => {
    if (RETAINED_EARNINGS_PATTERN.test(item.label)) return;
    if (NONCASH_EQUITY_PATTERN.test(item.label)) return;

    const curAmt: number = item.cur || 0;
    const priorAmt: number = (item.prior as number) || 0;
    const delta: number = curAmt - priorAmt;

    if (Math.abs(delta) < 0.005) return;

    if (delta > 0) {
      items.push({ label: 'Issuance of ' + item.label, amount: delta, priorAmount: 0 });
    } else {
      items.push({ label: 'Repurchase of ' + item.label, amount: delta, priorAmount: 0 });
    }
  });

  return items;
}

// Local NI calculation (avoids importing computeNetIncome to prevent circular deps)
function computeNetIncomeForCF(d: SectionData): number {
  const sumSec = (s: string): number => (d[s] || []).reduce(
    (acc: number, item: LineItem) => acc + (parseFloat(String(item.amount)) || 0), 0
  );
  return sumSec('revenue') - sumSec('cogs') - sumSec('opex') + sumSec('other');
}

// Detect if TB is closed (RE includes current period activity)
function isTBClosed(d: SectionData): boolean {
  const sumSec = (s: string): number => (d[s] || []).reduce(
    (acc: number, item: LineItem) => acc + (parseFloat(String(item.amount)) || 0), 0
  );
  const a = sumSec('current-assets') + sumSec('noncurrent-assets');
  const le = sumSec('current-liab') + sumSec('noncurrent-liab') + sumSec('equity');
  const ni = computeNetIncomeForCF(d);
  return Math.abs(a - le) < Math.abs(a - (le + ni));
}

// ─── Cash Flow (Indirect Method) ───────────────────────────────────────────

/**
 * Builds the statement of cash flows using the indirect method,
 * starting from net income.
 */
export function buildCashFlow(): void {
  prepareStatementData();
  try {
  const multi: boolean = hasPriorData();
  const netIncome: number = computeNetIncome(state.currentData);
  const pNetIncome: number = multi ? computeNetIncome(state.priorData) : 0;

  // Get manual cf-operating labels for deduplication
  const manualOpLabels: string[] = rows(state.currentData, 'cf-operating').map(
    (r: LineItem) => r.label
  );
  const pManualOpLabels: string[] = multi
    ? rows(state.priorData, 'cf-operating').map((r: LineItem) => r.label)
    : [];

  // Auto-detect non-cash items
  const noncashItems: NoncashItem[] = findNoncashItems(
    state.currentData,
    manualOpLabels
  );
  const pNoncashItems: NoncashItem[] = multi
    ? findNoncashItems(state.priorData, pManualOpLabels)
    : [];

  // Working capital changes (only when prior data exists)
  const wcChanges: WorkingCapitalChange[] = multi
    ? computeWorkingCapitalChanges(state.currentData, state.priorData)
    : [];

  // Manual operating items
  const manualOpItems: MergedItem[] = mergeLabels(
    rows(state.currentData, 'cf-operating'),
    multi ? rows(state.priorData, 'cf-operating') : [],
    multi
  );

  // Calculate totals
  const autoNoncashTotal: number = noncashItems.reduce(
    (s: number, i: NoncashItem) => s + i.amount,
    0
  );
  const pAutoNoncashTotal: number = pNoncashItems.reduce(
    (s: number, i: NoncashItem) => s + i.amount,
    0
  );
  const wcTotal: number = wcChanges.reduce(
    (s: number, i: WorkingCapitalChange) => s + i.amount,
    0
  );
  const manualOpTotal: number = sum(state.currentData, 'cf-operating');
  const pManualOpTotal: number = multi
    ? sum(state.priorData, 'cf-operating')
    : 0;

  const totalOp: number =
    netIncome + autoNoncashTotal + wcTotal + manualOpTotal;
  const pTotalOp: number = multi
    ? pNetIncome + pAutoNoncashTotal + pManualOpTotal
    : 0;

  // Auto-detect investing/financing activities (only with prior data)
  const autoInvItems: CashFlowLineItem[] = multi
    ? detectInvestingActivities(state.currentData, state.priorData)
    : [];
  const autoFinItems: CashFlowLineItem[] = multi
    ? detectFinancingActivities(state.currentData, state.priorData)
    : [];

  // Manual investing/financing items
  const manualInvItems: MergedItem[] = mergeLabels(
    rows(state.currentData, 'cf-investing'),
    multi ? rows(state.priorData, 'cf-investing') : [],
    multi
  );
  const manualFinItems: MergedItem[] = mergeLabels(
    rows(state.currentData, 'cf-financing'),
    multi ? rows(state.priorData, 'cf-financing') : [],
    multi
  );

  const autoInvTotal: number = autoInvItems.reduce(
    (s: number, i: CashFlowLineItem) => s + i.amount, 0
  );
  const autoFinTotal: number = autoFinItems.reduce(
    (s: number, i: CashFlowLineItem) => s + i.amount, 0
  );
  const manualInvTotal: number = sum(state.currentData, 'cf-investing');
  const manualFinTotal: number = sum(state.currentData, 'cf-financing');
  const pManualInvTotal: number = multi ? sum(state.priorData, 'cf-investing') : 0;
  const pManualFinTotal: number = multi ? sum(state.priorData, 'cf-financing') : 0;

  const totalInv: number = autoInvTotal + manualInvTotal;
  const totalFin: number = autoFinTotal + manualFinTotal;
  const pTotalInv: number = pManualInvTotal;
  const pTotalFin: number = pManualFinTotal;

  const netChange: number = totalOp + totalInv + totalFin;
  const pNetChange: number = multi ? pTotalOp + pTotalInv + pTotalFin : 0;

  let html: string = stmtHeader('Statement of Cash Flows');
  const tableClass: string = multi ? 'stmt-table multi-period' : 'stmt-table';
  if (multi) {
    html +=
      '<table class="' +
      tableClass +
      '"><colgroup><col class="col-label"><col class="col-current"><col class="col-prior"></colgroup><tbody>';
  } else {
    html +=
      '<table class="' +
      tableClass +
      '"><colgroup><col class="col-label"><col class="col-amount"><col class="col-total"></colgroup><tbody>';
  }
  html += columnHeaders(multi);

  // -- OPERATING ACTIVITIES (Indirect Method) --
  html += sectionHeader('Cash Flows from Operating Activities', multi);
  html += tr({ label: 'Net Income', indent: true, currentAmt: netIncome, priorAmt: multi ? pNetIncome : null, isTotal: false, cls: '', multi });

  // Non-cash adjustments
  if (noncashItems.length > 0 || (multi && pNoncashItems.length > 0)) {
    html +=
      '<tr class="section-header" style="padding-top:6px"><td colspan="3" style="font-size:0.78rem;font-style:italic;color:var(--muted)">Adjustments for non-cash items:</td></tr>';
    // Merge current and prior non-cash items for multi-period alignment
    if (multi) {
      const allNoncash: MergedItem[] = mergeLabels(
        noncashItems.map((i: NoncashItem) => ({
          label: i.label,
          amount: i.amount,
        })),
        pNoncashItems.map((i: NoncashItem) => ({
          label: i.label,
          amount: i.amount,
        })),
        true
      );
      allNoncash.forEach((item: MergedItem) => {
        html += tr({ label: item.label, indent: true, currentAmt: item.cur, priorAmt: item.prior, isTotal: false, cls: '', multi });
      });
    } else {
      noncashItems.forEach((item: NoncashItem) => {
        html += tr({ label: item.label, indent: true, currentAmt: item.amount, priorAmt: null, isTotal: false, cls: '', multi });
      });
    }
  }

  // Working capital changes
  if (wcChanges.length > 0) {
    html +=
      '<tr class="section-header" style="padding-top:6px"><td colspan="3" style="font-size:0.78rem;font-style:italic;color:var(--muted)">Changes in operating assets and liabilities:</td></tr>';
    wcChanges.forEach((item: WorkingCapitalChange) => {
      html += tr({ label: item.label, indent: true, currentAmt: item.amount, priorAmt: null, isTotal: false, cls: '', multi });
    });
  }

  // Manual operating items (if any)
  if (manualOpItems.length > 0) {
    html +=
      '<tr class="section-header" style="padding-top:6px"><td colspan="3" style="font-size:0.78rem;font-style:italic;color:var(--muted)">Other operating adjustments:</td></tr>';
    manualOpItems.forEach((item: MergedItem) => {
      html += tr({ label: item.label, indent: true, currentAmt: item.cur, priorAmt: item.prior, isTotal: false, cls: '', multi });
    });
  }

  html += tr({ label: 'Net Cash from Operating Activities', indent: false, currentAmt: totalOp, priorAmt: multi ? pTotalOp : null, isTotal: false, cls: '', multi });

  // -- INVESTING ACTIVITIES --
  html += sectionHeader('Cash Flows from Investing Activities', multi);
  const hasInvItems: boolean = autoInvItems.length > 0 || manualInvItems.length > 0;
  if (hasInvItems) {
    autoInvItems.forEach((item: CashFlowLineItem) => {
      html += tr({ label: item.label, indent: true, currentAmt: item.amount, priorAmt: multi ? item.priorAmount : null, isTotal: false, cls: '', multi });
    });
    manualInvItems.forEach((item: MergedItem) => {
      html += tr({ label: item.label, indent: true, currentAmt: item.cur, priorAmt: item.prior, isTotal: false, cls: '', multi });
    });
  } else {
    html +=
      '<tr class="item"><td class="col-label" style="padding-left:20px;font-style:italic;color:var(--muted)">None</td><td></td>' +
      (multi ? '' : '<td></td>') +
      '</tr>';
  }
  html += tr({ label: 'Net Cash from Investing Activities', indent: false, currentAmt: totalInv, priorAmt: multi ? pTotalInv : null, isTotal: false, cls: '', multi });

  // -- FINANCING ACTIVITIES --
  html += sectionHeader('Cash Flows from Financing Activities', multi);
  const hasFinItems: boolean = autoFinItems.length > 0 || manualFinItems.length > 0;
  if (hasFinItems) {
    autoFinItems.forEach((item: CashFlowLineItem) => {
      html += tr({ label: item.label, indent: true, currentAmt: item.amount, priorAmt: multi ? item.priorAmount : null, isTotal: false, cls: '', multi });
    });
    manualFinItems.forEach((item: MergedItem) => {
      html += tr({ label: item.label, indent: true, currentAmt: item.cur, priorAmt: item.prior, isTotal: false, cls: '', multi });
    });
  } else {
    html +=
      '<tr class="item"><td class="col-label" style="padding-left:20px;font-style:italic;color:var(--muted)">None</td><td></td>' +
      (multi ? '' : '<td></td>') +
      '</tr>';
  }
  html += tr({ label: 'Net Cash from Financing Activities', indent: false, currentAmt: totalFin, priorAmt: multi ? pTotalFin : null, isTotal: false, cls: '', multi });

  html += tr({ label: 'Net Change in Cash', indent: false, currentAmt: netChange, priorAmt: multi ? pNetChange : null, isTotal: true, cls: '', multi });

  html += '</tbody></table>';
  const cashflowEl = el('cashflow-statement');
  if (cashflowEl) cashflowEl.innerHTML = html;
  } finally { restoreStatementData(); }
}
